import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { pushToast } from '../lib/toast';

const PALETTE = ['#f59e0b', '#ef4444', '#22c55e', '#3b82f6', '#a855f7', '#ec4899', '#14b8a6', '#eab308'];

export function AreasView() {
  const qc = useQueryClient();
  const areas = useQuery({ queryKey: ['areas'], queryFn: api.listAreas });
  const [name, setName] = useState('');
  const [color, setColor] = useState(PALETTE[0]);

  const create = useMutation({
    mutationFn: () => api.createArea({ name: name.trim(), color }),
    onSuccess: () => {
      pushToast('Area added.', 'success');
      setName('');
      qc.invalidateQueries({ queryKey: ['areas'] });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: any }) => api.updateArea(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['areas'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteArea(id),
    onSuccess: () => {
      pushToast('Area deleted.');
      qc.invalidateQueries({ queryKey: ['areas'] });
    },
  });

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 md:py-8">
      <h1 className="text-2xl font-semibold tracking-tight mb-1">Areas</h1>
      <p className="text-sm text-zinc-500 mb-4">Buckets for projects. Keep it small — five is enough.</p>

      <ul className="divide-y divide-zinc-800 rounded-xl border border-zinc-800 overflow-hidden">
        {(areas.data ?? []).map((a) => (
          <li key={a.id} className="flex items-center gap-3 px-3 py-2">
            <input
              type="color"
              defaultValue={a.color ?? '#52525b'}
              className="h-6 w-6 rounded bg-transparent border-0 cursor-pointer p-0"
              onBlur={(e) => update.mutate({ id: a.id, patch: { color: e.target.value } })}
            />
            <input
              defaultValue={a.name}
              className="bg-transparent flex-1 outline-none text-sm"
              onBlur={(e) => e.target.value.trim() && update.mutate({ id: a.id, patch: { name: e.target.value.trim() } })}
            />
            <button onClick={() => confirm('Delete area?') && remove.mutate(a.id)} className="p-1.5 text-red-400 hover:bg-zinc-800 rounded">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ul>

      <form
        className="mt-4 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <input
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          className="h-9 w-9 rounded border-0 bg-transparent p-0"
        />
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New area name"
          className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500"
        />
        <button className="rounded-lg bg-amber-500 text-black px-3 py-2 text-sm font-medium">Add</button>
      </form>
    </div>
  );
}
